import { Link } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';

/**
 * App-shell banner for pending family invites.
 *
 * The rich accept/decline tiles live on the Families page
 * (`PendingInvitesBanner`), but reaching them requires already knowing to look:
 * the only shell-level signal was a dot on the Families nav item, which is easy
 * to miss and says nothing about what is waiting. This mirrors
 * `InboundRecoveryBanner` — same slot, same shape — so every "someone needs you
 * to do something" signal reads the same way.
 *
 * Deliberately not actionable here: accepting an invite hydrates family keys and
 * forces a full safe re-pull, which belongs with the tile that can report
 * per-invite errors. This banner only routes there.
 */
export function PendingInviteShellBanner(): JSX.Element | null {
  const { pendingInvites } = useSafe();
  if (pendingInvites.length === 0) return null;

  const first = pendingInvites[0];
  const more = pendingInvites.length - 1;
  const message = more > 0
    ? `You've been invited to join ${first.orgName} and ${more} other ${more === 1 ? 'family' : 'families'}.`
    : `You've been invited to join ${first.orgName}.`;

  return (
    <div className="bg-sky-500/25 border-b border-sky-500/60 text-hbs-text px-4 py-2 flex items-center justify-between">
      <div className="text-sm">
        <strong>Family invite:</strong>{' '}
        <span>{message}</span>
      </div>
      <Link
        to="/orgs"
        id="pending-invite-banner-review"
        className="text-sm font-semibold underline hover:text-hbs-primary shrink-0 whitespace-nowrap ml-4"
      >
        Review
      </Link>
    </div>
  );
}
